(() => {
  const theme = window.theme || {};

  function formatMoney(cents) {
    const value = Number(cents) || 0;
    if (theme.currency && theme.shopCurrency && theme.currency !== theme.shopCurrency) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: theme.currency }).format(value / 100);
      } catch (e) { /* fall through to the shop format */ }
    }
    const format = theme.moneyFormat || '${{amount}}';
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(decimals);
      const [int, frac] = fixed.split('.');
      const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? withSep + decimal + frac : withSep;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
        default: return fmt(value, 2);
      }
    });
  }

  async function cartRequest(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: 'cart-drawer', sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
    return data;
  }

  function showCart(data, opener) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && theme.cartDrawer && data.sections) {
      drawer.renderSections(data.sections);
      drawer.open(opener);
    } else {
      window.location.href = theme.routes.cart;
    }
  }

  /* Countdown bar */
  class SaleBar extends HTMLElement {
    connectedCallback() {
      const end = parseInt(this.dataset.end, 10);
      if (!end) return;
      this.endMs = end * 1000;
      this.units = {};
      this.querySelectorAll('[data-unit]').forEach((el) => { this.units[el.dataset.unit] = el; });
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      let left = Math.max(0, Math.floor((this.endMs - Date.now()) / 1000));
      if (left === 0) {
        clearInterval(this.timer);
        if (this.dataset.hideWhenDone === 'true') this.hidden = true;
      }
      const d = Math.floor(left / 86400); left -= d * 86400;
      const h = Math.floor(left / 3600); left -= h * 3600;
      const m = Math.floor(left / 60);
      const s = left - m * 60;
      const pad = (n) => String(n).padStart(2, '0');
      if (this.units.d) this.units.d.textContent = pad(d);
      if (this.units.h) this.units.h.textContent = pad(h);
      if (this.units.m) this.units.m.textContent = pad(m);
      if (this.units.s) this.units.s.textContent = pad(s);
    }
  }
  customElements.define('sale-bar', SaleBar);

  /* Gallery */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-viewport]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      if (this.slides.length < 2) {
        this.querySelectorAll('[data-prev],[data-next]').forEach((b) => { b.hidden = true; });
      }
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index() - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index() + 1));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.thumb))));
      this.viewport.addEventListener('scroll', () => {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => this.syncThumbs());
      }, { passive: true });
      this.viewport.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.go(this.index() + 1);
        if (e.key === 'ArrowLeft') this.go(this.index() - 1);
      });
    }
    index() {
      return Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
    }
    go(i) {
      const n = this.slides.length;
      if (!n) return;
      const target = (i + n) % n;
      this.viewport.scrollTo({ left: target * this.viewport.clientWidth, behavior: 'smooth' });
    }
    goToMedia(mediaId) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i >= 0) this.go(i);
    }
    syncThumbs() {
      const i = this.index();
      this.thumbs.forEach((t, n) => t.classList.toggle('is-active', n === i));
      const strip = this.querySelector('.gallery__thumbs');
      const thumb = this.thumbs[i];
      if (strip && thumb) strip.scrollTo({ left: thumb.offsetLeft - strip.clientWidth / 2 + thumb.clientWidth / 2, behavior: 'smooth' });
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* Delivery timeline */
  class DeliveryTimeline extends HTMLElement {
    connectedCallback() {
      const fmt = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { month: 'short', day: 'numeric' });
      // Business days, matching "ships in 1–2 business days" and "arrives in 7–12 business days".
      const add = (days) => {
        const d = new Date();
        for (let left = days; left > 0;) {
          d.setDate(d.getDate() + 1);
          if (d.getDay() !== 0 && d.getDay() !== 6) left -= 1;
        }
        return fmt.format(d);
      };
      const ds = this.dataset;
      const range = (a, b) => (Number(a) === Number(b) ? add(Number(a)) : `${add(Number(a))} – ${add(Number(b))}`);
      this.querySelector('[data-date="ordered"]').textContent = add(0);
      this.querySelector('[data-date="ready"]').textContent = range(ds.readyMin, ds.readyMax);
      this.querySelector('[data-date="delivered"]').textContent = range(ds.deliverMin, ds.deliverMax);
    }
  }
  customElements.define('delivery-timeline', DeliveryTimeline);

  /* Before / after */
  class BeforeAfter extends HTMLElement {
    connectedCallback() {
      const range = this.querySelector('input[type="range"]');
      range?.addEventListener('input', () => this.style.setProperty('--pos', `${range.value}%`));
    }
  }
  customElements.define('before-after', BeforeAfter);

  /* Product */
  class ProductPage extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      this.product = json ? JSON.parse(json.textContent) : null;
      this.form = this.querySelector('[data-product-form]');
      this.addButton = this.querySelector('[data-add]');
      this.idInput = this.querySelector('[data-variant-id]');
      this.error = this.querySelector('[data-error]');
      this.gallery = this.querySelector('media-gallery');
      this.bundleWrap = this.querySelector('[data-bundles-block]');
      this.picker = this.querySelector('[data-variant-picker]');
      this.addon = this.querySelector('[data-addon]');

      if (this.bundleWrap) this.initBundles();
      if (this.picker) this.picker.addEventListener('change', () => this.onOptionChange());
      if (this.form) this.form.addEventListener('submit', (e) => { e.preventDefault(); this.add(this.addButton); });
      this.initSticky();
    }

    variantById(id) {
      return this.product?.variants.find((v) => v.id === Number(id));
    }

    /* Bundles */
    initBundles() {
      this.bundles = [...this.bundleWrap.querySelectorAll('[data-bundle]')];
      this.bundleWrap.addEventListener('change', (e) => {
        if (e.target.matches('input[type="radio"]')) this.selectBundle(e.target.closest('[data-bundle]'));
        if (e.target.matches('[data-unit-variant]')) {
          e.target.dataset.touched = 'true';
          const v = this.variantById(e.target.value);
          if (v?.media_id && this.gallery) this.gallery.goToMedia(v.media_id);
          this.updateBundlePrices();
        }
      });
      this.updateBundlePrices();
    }

    selectedBundle() {
      return this.bundles.find((b) => b.querySelector('input[type="radio"]').checked) || this.bundles[0];
    }

    selectBundle(bundle) {
      this.bundles.forEach((b) => b.classList.toggle('is-selected', b === bundle));
      this.updateBundlePrices();
    }

    bundleVariantIds(bundle) {
      const selects = [...bundle.querySelectorAll('[data-unit-variant]')];
      if (selects.length) return selects.map((s) => Number(s.value));
      const id = Number(this.idInput?.value);
      return Array.from({ length: Number(bundle.dataset.qty) }, () => id);
    }

    setUntouchedUnits(variantId) {
      if (!this.bundleWrap) return;
      this.bundleWrap.querySelectorAll('[data-unit-variant]').forEach((select) => {
        if (select.dataset.touched) return;
        const option = [...select.options].find((o) => Number(o.value) === variantId && !o.disabled);
        if (option) select.value = String(variantId);
      });
      this.updateBundlePrices();
    }

    updateBundlePrices() {
      if (!this.product) return;
      const compareMode = this.bundleWrap.dataset.compareMode;
      this.bundles.forEach((bundle) => {
        const variants = this.bundleVariantIds(bundle).map((id) => this.variantById(id)).filter(Boolean);
        if (!variants.length) return;
        const discount = Number(bundle.dataset.discount) || 0;
        const base = variants.reduce((sum, v) => sum + v.price, 0);
        // Same math as a Shopify automatic discount: each item's discount is rounded down to the cent.
        const total = variants.reduce((sum, v) => sum + v.price - Math.floor(v.price * discount / 100), 0);
        const compare = compareMode === 'compare'
          ? variants.reduce((sum, v) => sum + Math.max(v.compare_at_price || 0, v.price), 0)
          : base;
        const totalEl = bundle.querySelector('[data-total]');
        const eachEl = bundle.querySelector('[data-each]');
        const compareEl = bundle.querySelector('[data-total-compare]');
        if (totalEl) totalEl.textContent = formatMoney(total);
        if (eachEl) eachEl.textContent = formatMoney(Math.round(total / variants.length));
        if (compareEl) {
          compareEl.textContent = formatMoney(compare);
          compareEl.hidden = compare <= total;
        }
        const savePill = bundle.querySelector('[data-save-amount]');
        if (savePill) {
          savePill.hidden = compare <= total;
          savePill.textContent = (savePill.dataset.template || '%').replace('%', formatMoney(compare - total).replace(/[.,]00(?!\d)/, ''));
        }
        bundle.querySelectorAll('[data-unit-variant]').forEach((select) => {
          const v = this.variantById(select.value);
          const thumb = select.parentElement.querySelector('[data-unit-thumb]');
          if (thumb) thumb.innerHTML = v?.image ? `<img src="${v.image}" alt="" width="40" height="40" loading="lazy">` : '';
        });
      });
      const selected = this.selectedBundle();
      const ids = selected ? this.bundleVariantIds(selected) : [];
      const soldOut = ids.some((id) => this.variantById(id) && !this.variantById(id).available);
      this.setButtonState(!soldOut);
      const totalText = selected?.querySelector('[data-total]')?.textContent;
      const stickyPrice = this.querySelector('[data-sticky-price]');
      const stickyQty = this.querySelector('[data-sticky-qty]');
      if (stickyPrice && totalText) stickyPrice.textContent = totalText;
      if (stickyQty && selected) stickyQty.textContent = Number(selected.dataset.qty) > 1 ? `${selected.dataset.qty} × · ` : '';
    }

    /* Variant picker */
    onOptionChange() {
      const selected = [...this.picker.querySelectorAll('input[type="radio"]:checked')].map((i) => i.value);
      this.picker.querySelectorAll('[data-option-label]').forEach((el, i) => { el.textContent = selected[i]; });
      const variant = this.product.variants.find((v) => v.product === this.product.variants[0].product && v.options.every((o, i) => o === selected[i]));
      if (!variant) { this.setButtonState(false); return; }
      this.idInput.value = variant.id;
      this.renderPrice(variant);
      this.renderStock(variant);
      if (variant.media_id && this.gallery) this.gallery.goToMedia(variant.media_id);
      if (this.bundleWrap) this.setUntouchedUnits(variant.id);
      else this.setButtonState(variant.available);
      const url = new URL(window.location.href);
      if (this.dataset.url && url.pathname.includes('/products/')) {
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
    }

    renderPrice(variant) {
      const price = this.querySelector('[data-price]');
      const compare = this.querySelector('[data-compare]');
      const save = this.querySelector('[data-save]');
      if (price) price.textContent = formatMoney(variant.price);
      const onSale = variant.compare_at_price > variant.price;
      if (compare) { compare.hidden = !onSale; compare.textContent = formatMoney(variant.compare_at_price); }
      if (save) {
        save.hidden = !onSale;
        if (onSale) {
          const pct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
          save.textContent = (save.dataset.template || '%').replace('%', pct);
        }
      }
      if (!this.bundleWrap) {
        const sticky = this.querySelector('[data-sticky-price]');
        if (sticky) sticky.textContent = formatMoney(variant.price);
      }
    }

    renderStock(variant) {
      const stock = this.querySelector('[data-stock]');
      if (!stock) return;
      const text = stock.querySelector('[data-stock-text]');
      const threshold = Number(stock.dataset.threshold);
      const low = variant.inventory !== null && variant.inventory > 0 && variant.inventory <= threshold;
      stock.classList.toggle('is-low', low);
      if (text) text.textContent = low ? stock.dataset.lowTemplate.replace('%', variant.inventory) : stock.dataset.okText;
    }

    setButtonState(available) {
      if (!this.addButton) return;
      this.addButton.disabled = !available;
      const label = this.addButton.querySelector('[data-add-label]');
      if (label) label.textContent = available ? this.addButton.dataset.label : theme.strings.soldOut;
    }

    /* Add to cart */
    lineItems() {
      const counts = new Map();
      const ids = this.bundleWrap ? this.bundleVariantIds(this.selectedBundle()) : [Number(this.idInput.value)];
      ids.forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));
      const items = [...counts].map(([id, quantity]) => ({ id, quantity }));
      if (this.addon?.checked) items.push({ id: Number(this.addon.value), quantity: 1 });
      const protection = theme.protection || {};
      if (protection.auto && protection.variant && !protectionDeclined() && !document.querySelector('[data-protection].is-on')) {
        items.push({ id: Number(protection.variant), quantity: 1 });
      }
      return items;
    }

    async add(button) {
      if (!this.addButton || this.addButton.disabled) return;
      const busy = button || this.addButton;
      busy.classList.add('is-loading');
      busy.setAttribute('aria-busy', 'true');
      if (this.error) this.error.hidden = true;
      try {
        const data = await cartRequest(`${theme.routes.cartAdd}.js`, { items: this.lineItems() });
        showCart(data, busy);
      } catch (err) {
        if (this.error) { this.error.textContent = err.message || theme.strings.error; this.error.hidden = false; }
        this.error?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } finally {
        busy.classList.remove('is-loading');
        busy.removeAttribute('aria-busy');
      }
    }

    /* Sticky bar */
    initSticky() {
      const bar = this.querySelector('[data-sticky-atc]');
      const target = this.querySelector('.buy') || this.querySelector('.product__info');
      if (!bar || !target) return;
      const button = bar.querySelector('[data-sticky-add]');
      const observer = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        button.tabIndex = show ? 0 : -1;
      });
      observer.observe(target);
      button.addEventListener('click', () => this.add(button));
    }
  }
  customElements.define('product-page', ProductPage);

  /* Cart drawer */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-close]')) { this.close(); return; }
        const qtyBtn = e.target.closest('[data-qty-change]');
        if (qtyBtn) {
          const line = qtyBtn.closest('[data-line]');
          this.change(line.dataset.line, Number(qtyBtn.dataset.qtyChange));
          return;
        }
        const upsell = e.target.closest('[data-upsell-add]');
        if (upsell) this.addUpsell(upsell);
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trapFocus(e);
      });
    }

    trapFocus(e) {
      const focusables = [...this.querySelectorAll('a[href], button:not([disabled]), select, input:not([type="hidden"])')].filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      document.body.classList.add('is-locked');
      requestAnimationFrame(() => this.querySelector('.drawer__panel')?.focus());
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      document.body.classList.remove('is-locked');
      this.opener?.focus?.({ preventScroll: true });
    }

    renderSections(sections) {
      const html = sections['cart-drawer'];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('cart-drawer');
      if (!fresh) return;
      const wasOpen = this.classList.contains('is-open');
      this.innerHTML = fresh.innerHTML;
      this.dataset.count = fresh.dataset.count;
      if (wasOpen) this.querySelector('.drawer__panel')?.focus();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = fresh.dataset.count;
        el.classList.toggle('is-empty', fresh.dataset.count === '0');
      });
      if (fresh.dataset.onlyProtection === 'true') removeProtection().then((data) => data && this.renderSections(data.sections));
    }

    async change(line, quantity) {
      this.classList.add('is-busy');
      try {
        const data = await cartRequest(`${theme.routes.cartChange}.js`, { line: Number(line), quantity: Math.max(0, quantity) });
        if (data.sections) this.renderSections(data.sections);
      } catch (err) {
        window.alert(err.message);
      } finally {
        this.classList.remove('is-busy');
      }
    }

    async addUpsell(button) {
      const input = this.querySelector('[data-upsell-variant]');
      if (!input) return;
      button.classList.add('is-loading');
      try {
        const data = await cartRequest(`${theme.routes.cartAdd}.js`, { items: [{ id: Number(input.value), quantity: 1 }] });
        if (data.sections) this.renderSections(data.sections);
      } catch (err) {
        window.alert(err.message);
      } finally {
        button.classList.remove('is-loading');
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* Reviews */
  class ReviewsList extends HTMLElement {
    connectedCallback() {
      this.perPage = Number(this.dataset.perPage) || 9;
      this.limit = this.perPage;
      this.filter = 'all';
      this.cards = [...this.querySelectorAll('[data-review]')];
      this.more = this.querySelector('[data-more]');
      this.dialog = this.querySelector('[data-zoom-dialog]');
      this.querySelectorAll('[data-filter]').forEach((chip) => chip.addEventListener('click', () => {
        this.filter = chip.dataset.filter;
        this.limit = this.perPage;
        this.querySelectorAll('[data-filter]').forEach((c) => {
          c.classList.toggle('is-active', c === chip);
          c.setAttribute('aria-pressed', String(c === chip));
        });
        this.render();
      }));
      this.more?.addEventListener('click', () => { this.limit += this.perPage; this.render(); });
      this.addEventListener('click', (e) => {
        const zoom = e.target.closest('[data-review-zoom]');
        if (!zoom || !this.dialog) return;
        const holder = this.dialog.querySelector('[data-zoom-img]');
        const img = document.createElement('img');
        img.src = zoom.dataset.reviewZoom;
        img.alt = zoom.querySelector('img')?.alt || '';
        holder.replaceChildren(img);
        this.dialog.showModal();
      });
      this.dialog?.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
      this.render();
    }

    render() {
      const matching = this.cards.filter((c) => this.filter === 'all' || c.dataset.photo === 'true');
      this.cards.forEach((c) => { c.hidden = true; });
      matching.slice(0, this.limit).forEach((c) => { c.hidden = false; });
      if (this.more) this.more.hidden = matching.length <= this.limit;
    }
  }
  customElements.define('reviews-list', ReviewsList);

  /* Shipping protection */
  function protectionDeclined() {
    try { return sessionStorage.getItem('protection-declined') === '1'; } catch (e) { return false; }
  }

  function setDeclined(value) {
    try {
      if (value) sessionStorage.setItem('protection-declined', '1');
      else sessionStorage.removeItem('protection-declined');
    } catch (e) { /* storage blocked: nothing to remember */ }
  }

  async function removeProtection() {
    const variant = theme.protection?.variant;
    if (!variant) return null;
    try {
      return await cartRequest(`${theme.routes.cartChange}.js`, { id: String(variant), quantity: 0 });
    } catch (e) {
      return null;
    }
  }

  document.addEventListener('change', async (e) => {
    const toggle = e.target.closest('[data-protection-toggle]');
    if (!toggle) return;
    const box = toggle.closest('[data-protection]');
    const variant = Number(box.dataset.variant);
    const on = toggle.checked;
    box.classList.toggle('is-on', on);
    toggle.disabled = true;
    setDeclined(!on);
    try {
      const data = on
        ? await cartRequest(`${theme.routes.cartAdd}.js`, { items: [{ id: variant, quantity: 1 }] })
        : await cartRequest(`${theme.routes.cartChange}.js`, { id: String(variant), quantity: 0 });
      const drawer = toggle.closest('cart-drawer');
      if (drawer && data.sections) drawer.renderSections(data.sections);
      else window.location.reload();
    } catch (err) {
      toggle.checked = !on;
      box.classList.toggle('is-on', !on);
      toggle.disabled = false;
      window.alert(err.message);
    }
  });

  const cartPage = document.querySelector('[data-cart-page][data-only-protection="true"]');
  if (cartPage) removeProtection().then(() => window.location.reload());

  /* Global clicks */
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-cart-toggle]');
    const drawer = document.querySelector('cart-drawer');
    if (toggle && drawer && theme.cartDrawer) {
      e.preventDefault();
      drawer.open(toggle);
      return;
    }
    const buyLink = e.target.closest('[data-scroll-to-buy]');
    if (buyLink) {
      const target = document.querySelector('[data-bundles-block]') || document.querySelector('.buy') || document.querySelector('product-page') || document.querySelector('.chooser');
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    const menu = document.querySelector('[data-menu-drawer][open]');
    if (menu && !menu.contains(e.target)) menu.removeAttribute('open');
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const menu = document.querySelector('[data-menu-drawer][open]');
    if (menu) { menu.removeAttribute('open'); menu.querySelector('summary')?.focus(); }
  });
})();
